/**
 * Release velocity for a table swipe, measured so that a JANKED main thread
 * cannot turn a real flick into a stationary one.
 *
 * ## Why this is its own rule
 *
 * `decideSwipe` projects where the felt would land under inertia:
 * `projected = offset + velocity × 499`. Velocity is therefore not a tie-break,
 * it is most of the decision — a 0.2px/ms flick projects ~100px. Report zero
 * and the projection collapses to raw distance, which means the player must
 * physically drag past half the screen (~195px on a 390px phone) before
 * anything commits. That is exactly the "you need to do a power swipe" and
 * "jumps back to the original table" players reported with four tables open.
 *
 * ## What went wrong with the previous measurement
 *
 * It kept ONE previous sample and derived velocity from that single pair, then
 * zeroed it whenever the last touchmove was more than 100ms before touchend, on
 * the reasoning that the finger must have stopped.
 *
 * The reasoning is sound for a still finger and wrong for a stalled thread. A
 * browser does not emit touchmove while the main thread is blocked — it
 * coalesces the moves away — so a genuinely fast flick that spans a >100ms
 * stall arrives looking exactly like a deliberate pause, and was zeroed. Four
 * open tables is precisely the condition that produces those stalls (each
 * background table's dispatch re-renders a whole hidden subtree), which is why
 * the failure clustered there and on slower Android hardware.
 *
 * ## The rule here
 *
 * Do not try to classify the gap. Measure across it and let the DISTANCE decide,
 * because that is the one thing the two cases genuinely disagree about:
 *
 *   - finger held still, then lifted → the release point sits where the last
 *     sample did, so dx ≈ 0 and the velocity falls out as ~0 on its own;
 *   - finger kept flicking through a stall → the release point is far from the
 *     last sample, so dx is large and the flick reads as a flick.
 *
 * No pause heuristic, no threshold to tune. `touchend` carries the position the
 * finger actually lifted at, so the release point is trustworthy even when
 * every intermediate move was dropped.
 *
 * Pure, so every branch is testable without a browser — same idiom as
 * `swipeCommit`, which consumes what this returns.
 */

export type TouchSample = {
  /** Clientward x in px. */
  x: number;
  /** Event timestamp in ms — `e.timeStamp`, NOT `Date.now()`. See below. */
  at: number;
};

/**
 * Smallest interval worth measuring over.
 *
 * The measurement must stay NEAR THE RELEASE. A real flick accelerates into the
 * lift, so averaging over a wide window blends the slow start with the fast
 * finish and under-reports the speed that actually matters — at 60fps a 120ms
 * average came out ~40% below the true release velocity, which is enough to
 * lose the commit on gestures that had always worked. (Regression shipped and
 * caught on device, 2026-09-09: swipes got WORSE on iOS, which had never had
 * the stall problem in the first place.)
 *
 * So the anchor is the NEWEST sample at least this far back — two frames at
 * 60fps. Far enough that integer-rounded coordinates and one jittery sample
 * cannot dominate, close enough that it is still the release being measured.
 */
export const VELOCITY_MIN_INTERVAL_MS = 25;

/**
 * How far back the measurement may reach when the window is empty because the
 * thread stalled. Past this the gesture is genuinely over — a finger resting
 * for a third of a second has no release speed whatever it did before that.
 */
export const VELOCITY_MAX_LOOKBACK_MS = 300;

/** Samples worth keeping. The window is time-based; this only bounds memory. */
export const MAX_VELOCITY_SAMPLES = 8;

/**
 * Append a sample and drop anything older than the lookback.
 *
 * Returns a NEW array rather than mutating, so the caller can hold it in a ref
 * without the trimming being observable mid-gesture.
 */
export const pushTouchSample = (
  samples: readonly TouchSample[],
  sample: TouchSample
): TouchSample[] => {
  const cutoff = sample.at - VELOCITY_MAX_LOOKBACK_MS;
  const kept = samples.filter((s) => s.at >= cutoff);
  kept.push(sample);
  return kept.length > MAX_VELOCITY_SAMPLES ? kept.slice(kept.length - MAX_VELOCITY_SAMPLES) : kept;
};

/**
 * Velocity in px/ms at the moment of release, positive = moving right.
 *
 * TIMING: every `at` must come from the event's own timestamp. Under a stall
 * touch events queue and then flush in a burst, so two moves 30ms apart in the
 * world run ~1ms apart in the handler — wall-clock deltas make the result
 * garbage in both directions, deflating a decisive flick or inflating a gesture
 * nobody made.
 *
 * @param samples  touchmove samples, oldest first
 * @param release  the touchend point
 */
export const releaseVelocityPxPerMs = (
  samples: readonly TouchSample[],
  release: TouchSample
): number => {
  if (!samples.length) return 0;

  // The finger has been resting: whatever it did before that, it has no release
  // speed now. This is the case the old >100ms pause guard was reaching for —
  // but stated as "no samples AT ALL recently" rather than "the last sample is
  // a bit old", which is what made it misread a stalled thread as a still hand.
  const newest = samples[samples.length - 1];
  if (release.at - newest.at > VELOCITY_MAX_LOOKBACK_MS) return 0;

  // Walk back from the newest sample and take the FIRST one far enough away to
  // give a stable interval. On a healthy device that lands ~2 frames back, so
  // the answer is essentially the instantaneous release velocity. Under a stall
  // there is only one recent sample and it is already far back, so the same
  // walk measures straight across the gap — no separate branch, and no window
  // to be wrong about in either direction.
  let anchor = samples[0];
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    if (release.at - samples[i].at >= VELOCITY_MIN_INTERVAL_MS) {
      anchor = samples[i];
      break;
    }
  }

  const dt = release.at - anchor.at;
  if (dt <= 0) return 0;
  return (release.x - anchor.x) / dt;
};
